import React, { useEffect, useMemo, useState } from "react";
import {
  getAnalyticsAgents,
  getAnalyticsActivity,
  getAnalyticsPipeline,
  getAnalyticsViewings,
  connectGoogleCalendar,
  getGoogleCalendarStatus,
  getGoogleCalendarUrl
} from "../api/client.js";

const PRESETS = [
  { key: "week", label: "This week" },
  { key: "lastWeek", label: "Last week" },
  { key: "month", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "quarter", label: "This quarter" },
  { key: "ytd", label: "Year to date" },
  { key: "last12", label: "Last 12 months" }
];

const contactTypes = [
  "",
  "SELLER",
  "BUYER",
  "BUYER_SELLER",
  "LEGAL",
  "AGENT",
  "DEVELOPER",
  "COMPANY",
  "INTEREST_REGISTERED"
];

const listingStatuses = [
  "",
  "COMING_SOON",
  "ACTIVE",
  "UNDER_OFFER",
  "SOLD",
  "WITHDRAWN"
];

function computeRange(preset) {
  const now = new Date();
  const range = { start: new Date(now), end: new Date(now) };
  switch (preset) {
    case "week": {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      range.start.setDate(diff);
      break;
    }
    case "lastWeek": {
      const day = now.getDay();
      const diff = now.getDate() - day - 6;
      range.start.setDate(diff);
      range.end = new Date(range.start);
      range.end.setDate(range.start.getDate() + 6);
      break;
    }
    case "month": {
      range.start.setDate(1);
      break;
    }
    case "lastMonth": {
      range.start.setMonth(now.getMonth() - 1, 1);
      range.end = new Date(range.start);
      range.end.setMonth(range.start.getMonth() + 1, 0);
      break;
    }
    case "quarter": {
      const quarter = Math.floor(now.getMonth() / 3);
      range.start.setMonth(quarter * 3, 1);
      break;
    }
    case "ytd": {
      range.start.setMonth(0, 1);
      break;
    }
    case "last12": {
      range.start.setFullYear(now.getFullYear() - 1);
      break;
    }
    default: {
      range.start.setDate(now.getDate() - 30);
    }
  }
  range.start.setHours(0, 0, 0, 0);
  range.end.setHours(23, 59, 59, 999);
  return range;
}

export default function CrmPipelinePage() {
  const [preset, setPreset] = useState("last12");
  const [range, setRange] = useState(() => computeRange("last12"));
  const [contactType, setContactType] = useState("");
  const [listingStatus, setListingStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activityData, setActivityData] = useState([]);
  const [viewingsData, setViewingsData] = useState([]);
  const [agentData, setAgentData] = useState([]);
  const [activityError, setActivityError] = useState("");
  const [viewingsError, setViewingsError] = useState("");
  const [agentError, setAgentError] = useState("");
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState("");

  const loadData = () => {
    setLoading(true);
    setError("");
    getAnalyticsPipeline({
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      contactType: contactType || undefined,
      listingStatus: listingStatus || undefined
    })
      .then(setData)
      .catch((err) => setError(err.message || "Unable to load pipeline"))
      .finally(() => setLoading(false));
  };

  const loadExtras = () => {
    const params = {
      start: range.start.toISOString(),
      end: range.end.toISOString()
    };
    setActivityError("");
    getAnalyticsActivity(params)
      .then(setActivityData)
      .catch((err) => setActivityError(err.message || "Unable to load activity"));
    setViewingsError("");
    getAnalyticsViewings(params)
      .then(setViewingsData)
      .catch((err) => setViewingsError(err.message || "Unable to load viewings"));
    setAgentError("");
    getAnalyticsAgents(params)
      .then(setAgentData)
      .catch((err) => setAgentError(err.message || "Unable to load agents"));
  };

  useEffect(() => {
    loadData();
  }, [range, contactType, listingStatus]);

  useEffect(() => {
    loadExtras();
  }, [range]);

  useEffect(() => {
    getGoogleCalendarStatus().then((status) => setGoogleConnected(status.connected));
  }, []);

  const handlePreset = (value) => {
    setPreset(value);
    setRange(computeRange(value));
  };

  const handleCustomRange = (field, value) => {
    setRange((prev) => {
      const next = { ...prev };
      next[field] = value ? new Date(value) : prev[field];
      if (field === "start") {
        next.start.setHours(0, 0, 0, 0);
      } else {
        next.end.setHours(23, 59, 59, 999);
      }
      return next;
    });
    setPreset("custom");
  };

  const avgViewingsPerListing = useMemo(() => {
    if (!data) return "0.0";
    const ratio = data.newListings.value ? data.viewings.value / data.newListings.value : 0;
    return ratio.toFixed(1);
  }, [data]);

  const pipelineDepth = useMemo(() => {
    if (!data) return "0";
    return `${Math.round((data.pipeline.listingCount / Math.max(1, data.newListings.value)) * 100)}%`;
  }, [data]);

  const cards = useMemo(() => {
    if (!data) return [];
    return [
      {
        title: "New leads",
        value: data.newLeads.value,
        subtitle: data.newLeads.percentChange
          ? `${data.newLeads.percentChange}% vs previous`
          : "No previous period"
      },
      {
        title: "New listings",
        value: data.newListings.value,
        subtitle: `Avg ask £${Math.round(data.newListings.avgAsk || 0).toLocaleString()}`
      },
      {
        title: "Viewings booked",
        value: data.viewings.value,
        subtitle: "Across all listings"
      },
      {
        title: "Offers received",
        value: data.offers.value,
        subtitle: "Total offers logged"
      },
      {
        title: "Deals completed",
        value: data.deals.value,
        subtitle: `£${data.deals.totalValue.toLocaleString()} total`
      },
      {
        title: "Pipeline value",
        value: `£${data.pipeline.value.toLocaleString()}`,
        subtitle: `${data.pipeline.listingCount} listings under offer`
      }
    ];
  }, [data]);

  const sections = [
    {
      title: "Activity timeline",
      description: "Leads, listings, viewings, and offers over time",
      type: "activity"
    },
    {
      title: "Viewings by listing",
      description: "Viewings logged in the window",
      type: "viewings"
    },
    {
      title: "Agent performance",
      description: "Activity and pipeline for your team",
      type: "agents"
    },
    {
      title: "Google Calendar",
      description: "Sync viewings with Google",
      type: "google"
    }
  ];

  const handleGoogleConnect = async () => {
    setGoogleLoading(true);
    setGoogleError("");
    try {
      const { url, state } = await getGoogleCalendarUrl();
      const popup = window.open(url, "_blank", "width=500,height=700");
      setTimeout(() => {
        popup?.close();
        connectGoogleCalendar({ code: "demo_code", state }).then(() => {
          setGoogleConnected(true);
        });
      }, 1500);
    } catch (err) {
      setGoogleError(err.message || "Unable to connect Google");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="sticky top-6 z-30 flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-6 shadow-[0_35px_60px_-40px_rgba(0,0,0,0.8)]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">View pipeline</p>
            <h1 className="text-3xl font-semibold text-white">Pipeline analytics</h1>
          </div>
          <div className="flex flex-wrap gap-2 text-xs uppercase tracking-[0.35em] text-slate-400">
            {PRESETS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => handlePreset(item.key)}
                className={`rounded-full border px-4 py-1 ${
                  preset === item.key
                    ? "border-emerald-400 bg-emerald-500/20 text-white"
                    : "border-white/15 text-white/70 hover:border-white/40"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs uppercase tracking-[0.35em] text-slate-400">
            Start
            <input
              type="date"
              value={range.start.toISOString().split("T")[0]}
              onChange={(event) => handleCustomRange("start", event.target.value)}
              className="mt-2 w-full rounded-3xl border border-white/15 bg-gradient-to-br from-slate-900/80 to-slate-950/40 px-3 py-2 text-sm text-white shadow-inner shadow-black/50"
            />
          </label>
          <label className="text-xs uppercase tracking-[0.35em] text-slate-400">
            End
            <input
              type="date"
              value={range.end.toISOString().split("T")[0]}
              onChange={(event) => handleCustomRange("end", event.target.value)}
              className="mt-2 w-full rounded-3xl border border-white/15 bg-gradient-to-br from-slate-900/80 to-slate-950/40 px-3 py-2 text-sm text-white shadow-inner shadow-black/50"
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs uppercase tracking-[0.35em] text-slate-400">
              Contact type
              <select
                value={contactType}
                onChange={(event) => setContactType(event.target.value)}
                className="mt-2 w-full rounded-3xl border border-white/15 bg-gradient-to-br from-slate-900/80 to-slate-950/40 px-3 py-2 text-sm text-white shadow-inner shadow-black/60"
              >
                {contactTypes.map((type) => (
                  <option key={type} value={type}>
                    {type || "All"}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs uppercase tracking-[0.35em] text-slate-400">
              Listing status
              <select
                value={listingStatus}
                onChange={(event) => setListingStatus(event.target.value)}
                className="mt-2 w-full rounded-3xl border border-white/15 bg-gradient-to-br from-slate-900/80 to-slate-950/40 px-3 py-2 text-sm text-white shadow-inner shadow-black/60"
              >
                {listingStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status || "All"}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </header>

      {loading && (
        <div className="rounded-3xl border border-white/10 bg-slate-900/40 px-6 py-10 text-center text-sm text-slate-400">
          Loading pipeline…
        </div>
      )}
      {error && (
        <div className="rounded-3xl border border-rose-500/40 bg-rose-500/10 px-6 py-4 text-sm text-rose-200">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <div
            key={card.title}
            className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.5)]"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{card.title}</p>
            <p className="mt-3 text-3xl font-semibold text-white">{card.value}</p>
            <p className="mt-2 text-sm text-slate-400">{card.subtitle}</p>
          </div>
        ))}
      </div>

      <section className="grid gap-4 lg:grid-cols-2">
        {sections.map((section) => (
          <div
            key={section.title}
            className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.5)]"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{section.title}</p>
              <span className="text-[11px] uppercase tracking-[0.35em] text-white/40">Updated just now</span>
            </div>
            <div className="mt-3 rounded-2xl border border-white/5 bg-gradient-to-br from-slate-950 to-slate-900 px-4 py-6 text-sm text-white/70">
              {section.type === "activity" && (
                <>
                  {activityError ? (
                    <p className="text-sm text-rose-200">{activityError}</p>
                  ) : (
                    <div className="grid grid-cols-7 gap-2">
                      {activityData.map((entry) => (
                        <div key={entry.date} className="h-24 rounded-2xl bg-white/5 p-1">
                          <div
                            className="h-full rounded-2xl bg-gradient-to-b from-emerald-500 to-emerald-700"
                            style={{
                              height: `${(entry.viewings /
                                Math.max(1, Math.max(...activityData.map((e) => e.viewings || 0)))) *
                                100}%`
                            }}
                          />
                          <p className="mt-2 text-[10px] text-slate-400">{entry.date.slice(5)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {section.type === "viewings" && (
                <>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{section.description}</p>
                  {viewingsError ? (
                    <p className="text-sm text-rose-200">{viewingsError}</p>
                  ) : (
                    <ul className="mt-3 space-y-2 text-xs text-slate-300">
                      {viewingsData.slice(0, 4).map((detail) => (
                        <li key={detail.listing.id}>
                          {detail.listing.refCode} • {detail.viewings} viewings • {detail.attendees} attendees
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {section.type === "agents" && (
                <>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{section.description}</p>
                  {agentError ? (
                    <p className="text-sm text-rose-200">{agentError}</p>
                  ) : (
                    <ul className="mt-3 space-y-2 text-xs text-slate-300">
                      {agentData.slice(0, 4).map((detail) => (
                        <li key={detail.agent}>
                          {detail.agent}: {detail.deals} deals • £{detail.pipelineValue.toLocaleString()} pipeline
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {section.type === "google" && (
                <>
                  {googleError && <p className="text-sm text-rose-200">{googleError}</p>}
                  <button
                    type="button"
                    onClick={handleGoogleConnect}
                    disabled={googleConnected || googleLoading}
                    className="rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white hover:border-emerald-400 hover:text-white hover:bg-emerald-500/20"
                  >
                    {googleConnected ? "Connected" : googleLoading ? "Connecting…" : "Connect Google Calendar"}
                  </button>
                  <p className="mt-2 text-[10px] text-slate-400">
                    {googleConnected ? "Calendar sync enabled" : "Click to start OAuth flow"}
                  </p>
                </>
              )}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
